import React, { useEffect, useState } from "react";
import { DollarSign, Loader2, X } from "lucide-react";
import api from "../lib/api";

const STAGE_LABEL = {
  LIVE_PRETEST: "Pré-Teste",
  LIVE_TEST: "Teste",
  LIVE_PRESCALE: "Pré-Escala",
  LIVE_SCALE: "Escala",
};

export default function RegisterSpendModal({ isOpen, onClose, onSuccess }) {
  const [creatives, setCreatives] = useState([]);
  const [creativeId, setCreativeId] = useState("");
  const [totalSpend, setTotalSpend] = useState("");
  const [notes, setNotes] = useState("");
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [cycleOptions, setCycleOptions] = useState([]);
  const [cycleId, setCycleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await api.get("/api/creatives/");
        const list = res.data?.creatives || [];
        setCreatives(list);
        if (list.length && !creativeId) setCreativeId(list[0].id || list[0]._id);
      } catch (_err) { /* ignore */ }
    })();
    setError("");
    setResult(null);
    setTotalSpend("");
    setNotes("");
    setCampaignOptions([]);
    setCampaignId("");
    setCycleOptions([]);
    setCycleId("");
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const submit = async () => {
    setError("");
    setResult(null);
    const value = parseFloat(String(totalSpend).replace(",", "."));
    if (!creativeId) { setError("Selecione o criativo."); return; }
    if (Number.isNaN(value) || value < 0) {
      setError("Informe o valor acumulado em reais (ex: 80,00).");
      return;
    }
    setLoading(true);
    try {
      const body = {
        creative_id: creativeId,
        total_spend: value,
      };
      if (campaignOptions.length > 1 && campaignId) body.campaign_id = campaignId;
      if (cycleId) body.cycle_id = cycleId;
      if (notes.trim()) body.notes = notes.trim();
      const res = await api.post("/api/spend/update", body);
      setResult(res.data);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.code === "campaign_ambiguous") {
          setCampaignOptions(detail.campaigns || []);
          setError("Mais de uma campanha para este criativo. Selecione a campanha.");
        } else if (detail.code === "cycle_ambiguous") {
          setCycleOptions(detail.cycles || []);
          setError("Mais de um ciclo Live ativo. Em qual operação?");
        } else if (detail.code === "no_active_cycle") {
          setError(detail.message || "Nenhum ciclo Live ativo. Inicie um ciclo primeiro.");
        } else {
          setError(detail.message || "Erro ao atualizar gasto.");
        }
      } else {
        setError(typeof detail === "string" ? detail : "Erro ao atualizar gasto.");
      }
    } finally {
      setLoading(false);
    }
  };

  const brl = (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      data-testid="register-spend-modal-overlay"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        data-testid="register-spend-modal"
        className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-md p-6 relative shadow-2xl"
      >
        <button
          data-testid="close-spend-modal-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-5 h-5 text-[#FF6B00]" />
          <h2 className="text-lg font-extrabold text-white">Registrar / Atualizar Gasto</h2>
        </div>
        <p className="text-[11px] text-gray-500 mb-4 leading-snug">
          Informe o <strong>valor total acumulado</strong> hoje no Meta Ads para
          este criativo (não é soma — é o valor final).
        </p>

        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">Criativo *</label>
            <select
              data-testid="spend-creative-select"
              value={creativeId}
              onChange={(e) => setCreativeId(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            >
              <option value="">— selecione —</option>
              {creatives.map((c) => (
                <option key={c.id || c._id} value={c.id || c._id}>
                  {c.publication_status === "published" ? "▶ " : "⏸ "}{c.name}
                </option>
              ))}
            </select>
            {(() => {
              const c = creatives.find((x) => (x.id || x._id) === creativeId);
              if (!c) return null;
              if (c.publication_status === "published") {
                return (
                  <div
                    data-testid="spend-attribution-hint"
                    className="mt-1.5 text-[10px] leading-tight text-emerald-300/90 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1"
                  >
                    <span className="font-bold">▶ Publicado</span> — o gasto será atribuído à BM + Conta vinculadas a este criativo.
                  </div>
                );
              }
              return (
                <div
                  data-testid="spend-attribution-hint"
                  className="mt-1.5 text-[10px] leading-tight text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1"
                >
                  <span className="font-bold">⏸ Pausado</span> — publique o criativo (BM + Conta) antes de registrar gasto novo.
                </div>
              );
            })()}
          </div>

          {campaignOptions.length > 1 && (
            <div>
              <label className="block text-[11px] font-bold text-gray-300 mb-1">Campanha</label>
              <select
                data-testid="spend-campaign-select"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
              >
                <option value="">— selecione —</option>
                {campaignOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.stage})</option>
                ))}
              </select>
            </div>
          )}

          {cycleOptions.length > 1 && (
            <div>
              <label className="block text-[11px] font-bold text-amber-300 mb-1">
                Em qual operação? *
              </label>
              <select
                data-testid="spend-cycle-select"
                value={cycleId}
                onChange={(e) => setCycleId(e.target.value)}
                className="w-full bg-[#0F1218] border border-amber-500/40 rounded-lg px-2 py-2 text-white"
              >
                <option value="">— selecione o ciclo —</option>
                {cycleOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || `Ciclo #${c.cycle_number}`} — {STAGE_LABEL[c.stage] || c.stage}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">
              Valor total gasto (R$) *
            </label>
            <input
              data-testid="spend-total-input"
              value={totalSpend}
              onChange={(e) => setTotalSpend(e.target.value)}
              inputMode="decimal"
              placeholder="80,00"
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">
              Observações <span className="text-gray-500">(opcional)</span>
            </label>
            <input
              data-testid="spend-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Nova campanha, criativo trocado, etc."
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            />
          </div>

          {error && <div data-testid="spend-error" className="text-xs text-red-400">{error}</div>}

          {result && (
            <div
              data-testid="spend-result"
              className="rounded-lg bg-[#0F1218] border border-emerald-500/30 p-3 text-[12px]"
            >
              <div className="font-extrabold text-emerald-300 mb-1">
                Gasto atualizado para {brl(result.total_spend)} (delta: {brl(result.delta)})
              </div>
              <div className="grid grid-cols-2 gap-1 text-gray-300">
                <div>ROI: <strong>{result.metrics?.roi}×</strong></div>
                <div>CPL: <strong>{brl(result.metrics?.cpl)}</strong></div>
                <div>Custo/Valores: <strong>{brl(result.metrics?.cost_per_valores)}</strong></div>
                <div>Custo/Venda: <strong>{brl(result.metrics?.cost_per_sale)}</strong></div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg bg-[#1F2330] border border-[#3E4760] text-gray-300 font-bold text-xs uppercase"
          >
            Fechar
          </button>
          <button
            data-testid="submit-spend-btn"
            onClick={submit}
            disabled={loading}
            className="flex-1 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase flex items-center justify-center gap-1 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Salvar gasto"}
          </button>
        </div>
      </div>
    </div>
  );
}
